import React, { useState, useEffect } from "react";
import { get } from "../../services/api";
import {
  compareCivilYmd,
  formatCivilDateMedium,
  normalizeBarTimeZone,
} from "../../utils/civilDateTime.js";

const ShowResponseViewModal = ({ isOpen, onClose, recurringGroupId }) => {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShows = async () => {
      if (!isOpen || !recurringGroupId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setShows([]);

        // Fetch all shows in this group (accepted and rejected)
        // The endpoint works for both artists and bar owners
        const response = await get(`/shows/group/${recurringGroupId}`);

        if (!response || !response.success) {
          // If group endpoint doesn't work for bar owners, try alternative
          // For now, we'll need to add a bar owner endpoint or use a different approach
          setError(response?.error || "Failed to load show details");
          setLoading(false);
          return;
        }

        if (!response.shows || !Array.isArray(response.shows)) {
          setError("Invalid response format");
          setLoading(false);
          return;
        }

        setShows(response.shows);
      } catch (err) {
        console.error("Error fetching show details:", err);
        setError(err.message || "Failed to load show details");
      } finally {
        setLoading(false);
      }
    };

    fetchShows();
  }, [isOpen, recurringGroupId]);

  const formatDate = (dateString, barTimeZone) =>
    formatCivilDateMedium(dateString, normalizeBarTimeZone(barTimeZone));

  const formatTime = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "pm" : "am";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes}${ampm}`;
  };

  if (!isOpen) {
    return null;
  }

  const firstShow = shows[0];
  const acceptedShows = shows.filter((s) => s.status === "accepted");
  const rejectedShows = shows.filter((s) => s.status === "rejected");
  const pendingShows = shows.filter((s) => s.status === "pending");

  return (
    <div
      className="invite-response-modal-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="invite-response-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="show-response-view-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}
      >
        <div className="invite-response-modal-header">
          <h2 id="show-response-view-modal-title">Show Response</h2>
          <button
            type="button"
            className="invite-response-modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div
          className="invite-response-modal-body"
          style={{ flex: 1, overflow: "auto" }}
        >
          {loading ? (
            <div className="invite-response-loading">
              <p>Loading show details...</p>
            </div>
          ) : error && shows.length === 0 ? (
            <div className="invite-response-error">
              <p>{error}</p>
              <button
                type="button"
                className="invite-response-close-btn"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          ) : shows.length > 0 ? (
            <>
              {error && (
                <div className="invite-response-error">
                  <p>{error}</p>
                </div>
              )}

              <div className="invite-response-content">
                {/* Consolidated header info */}
                <div className="invite-response-bars">
                  <h3>Show Details:</h3>
                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      marginBottom: "1rem",
                    }}
                  >
                    <li>
                      <strong>Venue:</strong> {firstShow.bar_name || "Unknown"}
                    </li>
                    {firstShow.bar_address && (
                      <li>
                        <strong>Address:</strong> {firstShow.bar_address}
                      </li>
                    )}
                    <li>
                      <strong>Time:</strong> {formatTime(firstShow.start_time)}{" "}
                      - {formatTime(firstShow.end_time)}
                    </li>
                    {firstShow.base_pay !== undefined && (
                      <li>
                        <strong>Base Pay:</strong> ${firstShow.base_pay} per
                        show
                      </li>
                    )}
                    <li>
                      <strong>Artist:</strong>{" "}
                      {firstShow.stage_name || "Unknown"}
                    </li>
                  </ul>
                </div>

                {/* Summary */}
                <div style={{ marginTop: "1.5rem", marginBottom: "1rem" }}>
                  <p>
                    <strong>Accepted:</strong> {acceptedShows.length} show(s) |{" "}
                    <strong>Rejected:</strong> {rejectedShows.length} show(s)
                    {pendingShows.length > 0 && (
                      <>
                        {" "}
                        | <strong>Pending:</strong> {pendingShows.length}{" "}
                        show(s)
                      </>
                    )}
                  </p>
                </div>

                {/* Scrollable list of shows with status */}
                <div style={{ marginTop: "1.5rem" }}>
                  <h3>Show Status:</h3>
                  <div
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      border: "1px solid var(--border-gray)",
                      borderRadius: "8px",
                      padding: "0.5rem",
                    }}
                  >
                    {shows
                      .sort((a, b) => compareCivilYmd(a.date, b.date))
                      .map((show) => {
                        const isAccepted = show.status === "accepted";
                        const isRejected = show.status === "rejected";
                        const isPending = show.status === "pending";
                        return (
                          <div
                            key={show.show_id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "0.75rem",
                              borderRadius: "4px",
                              marginBottom: "0.25rem",
                              background: isAccepted
                                ? "rgba(16, 185, 129, 0.1)"
                                : isRejected
                                  ? "rgba(239, 68, 68, 0.1)"
                                  : isPending
                                    ? "rgba(250, 82, 82, 0.1)"
                                    : "transparent",
                            }}
                          >
                            <span style={{ flex: 1 }}>
                              <strong>
                                {formatDate(show.date, show.bar_time_zone)}
                              </strong>
                            </span>
                            <span
                              style={{
                                padding: "0.25rem 0.75rem",
                                borderRadius: "4px",
                                fontSize: "0.85rem",
                                fontWeight: 600,
                                background: isAccepted
                                  ? "rgba(16, 185, 129, 0.2)"
                                  : isRejected
                                    ? "rgba(239, 68, 68, 0.2)"
                                    : isPending
                                      ? "rgba(250, 82, 82, 0.2)"
                                      : "rgba(156, 163, 175, 0.2)",
                                color: isAccepted
                                  ? "#10b981"
                                  : isRejected
                                    ? "#ef4444"
                                    : isPending
                                      ? "#fa5252"
                                      : "#9ca3af",
                              }}
                            >
                              {isAccepted
                                ? "✓ Accepted"
                                : isRejected
                                  ? "✗ Rejected"
                                  : isPending
                                    ? "⏳ Pending"
                                    : "Unknown"}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>

              <div className="invite-response-actions">
                <button
                  type="button"
                  className="invite-response-close-btn"
                  onClick={onClose}
                  style={{ width: "100%" }}
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <div className="invite-response-error">
              <p>No shows found</p>
              <button
                type="button"
                className="invite-response-close-btn"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShowResponseViewModal;
